import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { apiFetchBlob } from '../../lib/api.js';

const apiPath = (src: string) => src.replace(/^\/api\/v1/, '');

interface ImageCache {
  load(src: string): Promise<string>;
  forget(src: string, objectUrl: string): void;
  open(): void;
  clear(): void;
}

function createImageCache(): ImageCache {
  const entries = new Map<string, Promise<string>>();
  const objectUrls = new Set<string>();
  let cleared = false;
  return {
    load(src) {
      let entry = entries.get(src);
      if (!entry) {
        entry = apiFetchBlob(apiPath(src)).then((blob) => {
          const url = URL.createObjectURL(blob);
          // A fetch that lands after the view closed is freed at once.
          if (cleared) URL.revokeObjectURL(url);
          else objectUrls.add(url);
          return url;
        });
        entry.catch(() => entries.delete(src));
        entries.set(src, entry);
      }
      return entry;
    },
    forget(src, objectUrl) {
      entries.delete(src);
      objectUrls.delete(objectUrl);
      URL.revokeObjectURL(objectUrl);
    },
    open() {
      cleared = false;
    },
    clear() {
      cleared = true;
      for (const url of objectUrls) URL.revokeObjectURL(url);
      objectUrls.clear();
      entries.clear();
    },
  };
}

const ImageCacheContext = createContext<ImageCache | null>(null);

/** One fetch per image for everything under it; every copy is freed when it unmounts (sign-out included) or the scope changes. */
export function ImageCacheProvider({ scope, children }: { scope?: string; children: ReactNode }) {
  const [current, setCurrent] = useState(() => ({ scope, cache: createImageCache() }));
  if (current.scope !== scope) setCurrent({ scope, cache: createImageCache() });
  const { cache } = current;
  // Re-opened on every setup: StrictMode replays the cleanup while the provider stays mounted.
  useEffect(() => {
    cache.open();
    return () => cache.clear();
  }, [cache]);
  return <ImageCacheContext.Provider value={cache}>{children}</ImageCacheContext.Provider>;
}

/** Warms the cache for an image about to show; does nothing outside an ImageCacheProvider. */
export function usePreloadImage(): (src: string) => void {
  const cache = useContext(ImageCacheContext);
  return useCallback(
    (src: string) => {
      void cache?.load(src).catch(() => undefined);
    },
    [cache],
  );
}

export function AuthenticatedImage({
  src,
  alt,
  className,
  onError,
  onLoad,
}: {
  src: string;
  alt: string;
  className?: string;
  onError?: () => void;
  onLoad?: () => void;
}) {
  const cache = useContext(ImageCacheContext);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  // A ref, so a caller's new callback never refetches the image.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let active = true;
    let owned: string | null = null;
    const loaded = cache
      ? cache.load(src)
      : apiFetchBlob(apiPath(src)).then((blob) => {
          owned = URL.createObjectURL(blob);
          if (!active) URL.revokeObjectURL(owned);
          return owned;
        });
    loaded
      .then((url) => {
        if (active) setObjectUrl(url);
      })
      .catch(() => {
        if (!active) return;
        setObjectUrl(null);
        onErrorRef.current?.();
      });
    return () => {
      active = false;
      // Without a cache this mount owns its copy; with one, the provider frees it.
      if (owned) URL.revokeObjectURL(owned);
      setObjectUrl(null);
    };
  }, [cache, src]);

  // Bytes the browser cannot decode fail here, not in the fetch; the copy goes so a later mount fetches again.
  const handleDecodeError = () => {
    if (objectUrl) {
      if (cache) cache.forget(src, objectUrl);
      else URL.revokeObjectURL(objectUrl);
    }
    setObjectUrl(null);
    onErrorRef.current?.();
  };

  return objectUrl ? (
    <img
      src={objectUrl}
      alt={alt}
      className={className}
      onError={handleDecodeError}
      onLoad={onLoad}
    />
  ) : null;
}
